import { useMemo, useState } from 'react';
import styles from './OsaMapSection.module.css';
import { IndiaMap } from '@/components/charts/IndiaMap';
import { osaBand, useOsaMap, type MapCity } from '@/hooks/useOsaMap';
import { titleCase } from '@/lib/format';
import type { DateRange } from '@/lib/dateRange';

/**
 * OSA View — Figma 2881:30849, the map section beneath the three tables.
 *
 * Two halves of one answer: the map says WHERE availability is weak, the
 * breakdown says by how much. Selecting on either side highlights the other.
 */
const BANDS = [
  { key: 'high', label: 'High ≥ 75%' },
  { key: 'mid', label: 'Mid 65-74%' },
  { key: 'low', label: 'Low < 65%' },
] as const;

type SortKey = 'sales' | 'wtOsa' | 'listing';
const SORTS: Array<{ key: SortKey; label: string }> = [
  { key: 'sales', label: 'Sales' },
  { key: 'wtOsa', label: 'Wt OSA%' },
  { key: 'listing', label: 'Listing%' },
];

const pct = (v: number | null): string => (v === null ? '—' : `${v.toFixed(1)}%`);
const money = (v: number): string =>
  v >= 1e7 ? `₹${(v / 1e7).toFixed(2)} Cr` : v >= 1e5 ? `₹${(v / 1e5).toFixed(2)} L` : `₹${Math.round(v).toLocaleString('en-IN')}`;

export function OsaMapSection({
  customerName,
  scope,
  range,
  onCityPick,
}: {
  customerName: string;
  scope: { category?: string | undefined; city?: string | undefined; sku?: string | undefined };
  range: DateRange;
  onCityPick?: (city: string) => void;
}) {
  const q = useOsaMap(customerName, scope, range, customerName !== '');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('sales');
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [zoom, setZoom] = useState(1);

  const cities = useMemo(() => q.data ?? [], [q.data]);

  const listed = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = term ? cities.filter((c) => c.city.includes(term)) : cities;
    return [...rows].sort((a, b) => {
      if (sort === 'sales') return b.sales - a.sales;
      // A city with no value sorts last either way — it is not "0%", it is
      // unknown, and ranking it as the worst would be a claim we cannot make.
      const av = a[sort], bv = b[sort];
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return bv - av;
    });
  }, [cities, search, sort]);

  const scopeLabel = scope.city ? titleCase(scope.city) ?? scope.city : 'All Cities';

  return (
    <section className={styles.wrap} aria-label="OSA view by city">
      <header className={styles.head}>
        <h2 className={styles.title}>
          OSA View - <span className={styles.scope}>{scopeLabel}</span>
        </h2>
        <div className={styles.legend}>
          {BANDS.map((b) => (
            <span key={b.key} className={styles.legendItem}>
              <span className={`${styles.dot} ${styles[b.key]}`} />
              {b.label}
            </span>
          ))}
          <span className={styles.legendSep} />
          <span className={styles.legendItem}>
            <span className={`${styles.dot} ${styles.sizeDot}`} />
            Circle Size = Sales Vol
          </span>
        </div>
      </header>

      <div className={styles.body}>
        <div className={styles.mapPane}>
          {q.isLoading && <p className={styles.state}>Loading cities…</p>}
          {q.isError && <p className={styles.state}>{q.error.message}</p>}
          {!q.isLoading && !q.isError && cities.length === 0 && (
            <p className={styles.state}>No city data for this scope.</p>
          )}
          {!q.isLoading && !q.isError && cities.length > 0 && (
            <>
              <div className={styles.zoom}>
                <button type="button" onClick={() => { setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2))); }} aria-label="Zoom out">−</button>
                <span className={styles.zoomSep} />
                <button type="button" onClick={() => { setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2))); }} aria-label="Zoom in">+</button>
              </div>
              <IndiaMap
                cities={cities}
                zoom={zoom}
                {...(selected ? { selected } : {})}
                onSelect={(c) => { setSelected(c === selected ? undefined : c); }}
              />
            </>
          )}
        </div>

        <aside className={styles.panel}>
          <h3 className={styles.panelTitle}>City Breakdown</h3>
          <input
            className={styles.search}
            placeholder="Search for Region"
            value={search}
            onChange={(e) => { setSearch(e.target.value); }}
            aria-label="Search for region"
          />
          <div className={styles.sorts} role="tablist">
            {SORTS.map((s) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={sort === s.key}
                className={`${styles.sortBtn} ${sort === s.key ? styles.sortOn : ''}`}
                onClick={() => { setSort(s.key); }}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className={styles.list}>
            {listed.length === 0 && <p className={styles.state}>No city matches “{search}”.</p>}
            {listed.map((c) => (
              <CityCard
                key={c.city}
                city={c}
                active={selected === c.city}
                onClick={() => {
                  setSelected(c.city === selected ? undefined : c.city);
                  onCityPick?.(c.city);
                }}
              />
            ))}
          </div>
        </aside>
      </div>
    </section>
  );
}

function CityCard({ city, active, onClick }: { city: MapCity; active: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`${styles.card} ${active ? styles.cardOn : ''}`} onClick={onClick}>
      <span className={styles.cardHead}>
        <span className={`${styles.dot} ${styles[osaBand(city.wtOsa)]}`} />
        {titleCase(city.city) ?? city.city}
      </span>
      <span className={styles.rows}>
        <span className={styles.row}><span>Wt OSA%</span><b>{pct(city.wtOsa)}</b></span>
        <span className={styles.row}><span>Listing %</span><b>{pct(city.listing)}</b></span>
        <span className={styles.row}><span>OSA in List%</span><b>{pct(city.osaInLs)}</b></span>
        <span className={styles.row}><span>Sales</span><b>{money(city.sales)}</b></span>
      </span>
    </button>
  );
}
